import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Rocket, TrendingUp, CreditCard, Play, FileDown, ChevronUp, BarChart3, Coins, type LucideIcon } from 'lucide-react';
import { track } from '../utils/analytics';
import { AnalyticsEvents } from '../utils/analyticsEvents';

interface QuickAction {
  id: string;
  label: string;
  icon: LucideIcon;
  path: string;
  colorClass: string;
}

const QuickActionsFloatingBar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [playLanding, setPlayLanding] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();

  const baseActions: QuickAction[] = [
    { id: 'new-campaign', label: 'New Campaign', icon: Rocket, path: '/campaigns/create', colorClass: 'from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700' },
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/dashboard', colorClass: 'from-sky-500 to-indigo-600 hover:from-sky-600 hover:to-indigo-700' },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp, path: '/analytics', colorClass: 'from-emerald-600 to-green-700 hover:from-emerald-700 hover:to-green-800' },
    { id: 'credits', label: 'Credits', icon: Coins, path: '/credits', colorClass: 'from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700' },
    { id: 'subscription', label: 'Subscription', icon: CreditCard, path: '/subscription', colorClass: 'from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800' },
  ];

  const runActions: QuickAction[] = runId
    ? [
        { id: 'progress', label: 'Progress', icon: Play, path: `/runs/${runId}/progress`, colorClass: 'from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700' },
        { id: 'export', label: 'Export Data', icon: FileDown, path: `/runs/${runId}`, colorClass: 'from-emerald-600 to-green-700 hover:from-emerald-700 hover:to-green-800' },
      ]
    : [];

  const actions = [...runActions, ...baseActions];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
        setPlayLanding(true);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleAction = (path: string) => {
    track(AnalyticsEvents.QUICK_ACTION_CLICK, { action: path });
    navigate(path);
    setOpen(false);
    setPlayLanding(true);
  };

  const handleToggle = () => {
    if (open) {
      setOpen(false);
      setPlayLanding(true);
    } else {
      setPlayLanding(false);
      setOpen(true);
    }
  };

  return (
    <div ref={panelRef} className="fixed right-8 bottom-8 z-40 flex flex-col items-end">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            className="mb-2 flex flex-col gap-1.5 rounded-xl border border-slate-600/50 bg-slate-800/95 backdrop-blur-xl p-2 shadow-xl"
          >
            {actions.map((action, i) => {
              const Icon = action.icon;
              return (
                <motion.button
                  key={action.id}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ type: 'spring', stiffness: 400, damping: 26, delay: 0.02 * i }}
                  onClick={() => handleAction(action.path)}
                  title={action.label}
                  className={`flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-r ${action.colorClass} text-white transition-all hover:shadow-lg`}
                >
                  <Icon className="h-5 w-5 shrink-0" size={20} />
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={handleToggle}
        className="relative flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 text-white shadow-lg transition-all hover:from-cyan-600 hover:to-blue-700 hover:shadow-xl overflow-visible"
        title="Quick Actions"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.span
              key="chevron"
              initial={{ opacity: 0, rotate: -90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="flex items-center justify-center"
            >
              <ChevronUp className="h-6 w-6" size={24} />
            </motion.span>
          ) : (
            <motion.span
              key="rocket"
              initial={
                playLanding
                  ? { y: -48, opacity: 0.85, rotate: 180, scale: 0.9 }
                  : false
              }
              animate={{ y: 0, opacity: 1, rotate: 0, scale: 1 }}
              exit={{ y: -48, opacity: 0, rotate: -60, scale: 0.85 }}
              transition={
                playLanding
                  ? {
                      type: 'tween',
                      duration: 0.35,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }
                  : {
                      type: 'tween',
                      duration: 0.3,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    }
              }
              onAnimationComplete={() => setPlayLanding(false)}
              className="flex items-center justify-center absolute inset-0"
            >
              <Rocket className="h-6 w-6 shrink-0" size={24} />
            </motion.span>
          )}
        </AnimatePresence>
      </button>
    </div>
  );
};

export default QuickActionsFloatingBar;
